// Animated "synth wave" backdrop — eight low-opacity horizontal sine-ish lines
// drifting at independent speeds, evoking a quiet oscilloscope. Pure SVG + CSS
// keyframes (no canvas/WebGL). Shared by the homepage testimonials section and
// the Become-a-Creator "Why Fivestar" section so both read as the same material.

interface BuildWavePathOptions {
  width: number;
  baselineY: number;
  amplitude: number;
  cycles: number;
  isStartingUp: boolean;
}

/**
 * Build an SVG path string approximating a sine wave with cubic bezier segments.
 * The path spans x∈[0, 2*width] with `cycles` complete wavelengths in [0, width],
 * so animating translateX from 0 to -width produces a seamless loop.
 *
 * Each half-wavelength is one cubic-bezier segment whose control points overshoot
 * the desired amplitude by 1/0.75 so the curve peaks at exactly the target amp.
 */
const buildWavePath = (opts: BuildWavePathOptions): string => {
  const { width, baselineY, amplitude, cycles, isStartingUp } = opts;
  const wavelength = width / cycles;
  const half = wavelength / 2;
  const ctrlOvershoot = amplitude / 0.75; // cubic-bezier peak factor
  const halfSteps = Math.round((2 * width) / half); // cover [0, 2*width]
  let path = `M 0 ${baselineY}`;
  let dir = isStartingUp ? -1 : 1; // -1 = peak up (smaller Y), 1 = peak down (larger Y)
  for (let i = 0; i < halfSteps; i++) {
    const x0 = i * half;
    const x1 = x0 + half;
    const cpY = baselineY + dir * ctrlOvershoot;
    const cp1x = x0 + half * 0.25;
    const cp2x = x0 + half * 0.75;
    path += ` C ${cp1x.toFixed(2)} ${cpY.toFixed(2)}, ${cp2x.toFixed(2)} ${cpY.toFixed(2)}, ${x1.toFixed(2)} ${baselineY.toFixed(2)}`;
    dir *= -1;
  }
  return path;
};

const WAVE_VIEW_W = 1440;
const WAVE_VIEW_H = 900;

interface WaveSpec {
  baselineY: number;
  amplitude: number;
  cycles: number;
  isStartingUp: boolean;
  stroke: string;
  strokeWidth: number;
  durationSec: number;
}

// Hand-tuned wave stack: 8 horizontal sine-ish lines stacked top→bottom across
// the section. Amplitudes, frequencies, and speeds are deliberately mismatched
// so the lines interfere visually like a quiet oscilloscope. Two carry a faint
// brand tint (cyan, lime); the rest are neutral black at very low opacity.
const WAVE_SPECS: WaveSpec[] = [
  { baselineY: 90,  amplitude: 18, cycles: 2.0, isStartingUp: true,  stroke: "rgba(0,0,0,0.05)",         strokeWidth: 1,    durationSec: 72 },
  { baselineY: 180, amplitude: 32, cycles: 1.5, isStartingUp: false, stroke: "rgba(0,0,0,0.045)",        strokeWidth: 1.25, durationSec: 96 },
  { baselineY: 285, amplitude: 14, cycles: 3.0, isStartingUp: true,  stroke: "rgba(68,194,227,0.07)",    strokeWidth: 1,    durationSec: 54 },
  { baselineY: 380, amplitude: 26, cycles: 2.5, isStartingUp: false, stroke: "rgba(0,0,0,0.055)",        strokeWidth: 1,    durationSec: 84 },
  { baselineY: 470, amplitude: 10, cycles: 4.0, isStartingUp: true,  stroke: "rgba(0,0,0,0.04)",         strokeWidth: 1,    durationSec: 42 },
  { baselineY: 560, amplitude: 36, cycles: 1.0, isStartingUp: false, stroke: "rgba(0,0,0,0.06)",         strokeWidth: 1.5,  durationSec: 110 },
  { baselineY: 660, amplitude: 20, cycles: 2.5, isStartingUp: true,  stroke: "rgba(192,255,0,0.07)",     strokeWidth: 1,    durationSec: 66 },
  { baselineY: 770, amplitude: 28, cycles: 1.75, isStartingUp: false, stroke: "rgba(0,0,0,0.05)",        strokeWidth: 1.25, durationSec: 90 },
];

const WAVE_CSS = `
  @keyframes wave-drift {
    from { transform: translate3d(0, 0, 0); }
    to   { transform: translate3d(-${WAVE_VIEW_W}px, 0, 0); }
  }
  @keyframes wave-drift-reverse {
    from { transform: translate3d(-${WAVE_VIEW_W}px, 0, 0); }
    to   { transform: translate3d(0, 0, 0); }
  }
  .wave-group {
    will-change: transform;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .wave-group { animation: none !important; transform: none !important; }
  }
`;

// Radial mask fades the wave texture out toward the bottom: opaque at top-center,
// gradually transparent, fully invisible by ~85% down — so the lines read as a
// surface texture rather than a hard graphic edge. (Matches the LeadFormSection
// treatment so adjacent sections feel of-a-piece.)
const DEFAULT_MASK =
  "radial-gradient(ellipse 200% 100% at 50% 0%, black 0%, black 30%, transparent 85%)";

interface WaveBackdropProps {
  /** Extra classes on the (absolutely positioned) wave layer wrapper. */
  className?: string;
  /** CSS mask-image applied to the wave layer; defaults to a top→bottom radial fade. */
  mask?: string;
}

/**
 * Render as the first child of a `relative overflow-hidden` section, with the
 * real content in a sibling marked `relative` so it stacks above the waves.
 */
export function WaveBackdrop({ className, mask = DEFAULT_MASK }: WaveBackdropProps) {
  return (
    <div
      aria-hidden="true"
      className={`absolute inset-0 pointer-events-none${className ? ` ${className}` : ""}`}
      style={{ maskImage: mask, WebkitMaskImage: mask }}
    >
      <style>{WAVE_CSS}</style>
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox={`0 0 ${WAVE_VIEW_W} ${WAVE_VIEW_H}`}
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {WAVE_SPECS.map((spec, i) => {
          // Alternate drift direction so adjacent waves move opposite ways;
          // creates a richer interference pattern at no perf cost.
          const reverse = i % 2 === 1;
          const d = buildWavePath({
            width: WAVE_VIEW_W,
            baselineY: spec.baselineY,
            amplitude: spec.amplitude,
            cycles: spec.cycles,
            isStartingUp: spec.isStartingUp,
          });
          return (
            <g
              key={i}
              className="wave-group"
              style={{
                animationName: reverse ? "wave-drift-reverse" : "wave-drift",
                animationDuration: `${spec.durationSec}s`,
              }}
            >
              <path
                d={d}
                fill="none"
                stroke={spec.stroke}
                strokeWidth={spec.strokeWidth}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default WaveBackdrop;
